import { useMutation, useQuery } from "@tanstack/react-query";
import { Download } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  descargarSerieCauchyXlsx,
  getPuntosCauchy,
  getSerieCauchy,
  llaveCauchy,
  type PuntoSerieCauchy,
} from "@/lib/api";

/**
 * Reportes → Parámetros Cauchy (DART).
 *
 * Consulta de location y scale diarios que guarda docs/DART_Cauchy.R en
 * etrm.dart_cauchy_diario (ventana móvil; la fecha es el último día de la
 * ventana). Los puntos se administran en Configuración → Nodos Cauchy.
 *
 * Location y scale van en DOS gráficas con su propio eje (no comparten
 * escala). El color sigue al punto según su orden en la lista completa, así
 * que filtrar no repinta a los demás.
 */

// Paleta categórica validada (orden fijo; ver skill dataviz). Pasa CVD y
// separación normal; tres tonos quedan bajo 3:1 contra blanco, por eso la
// página siempre lleva leyenda y tabla.
const PALETA = ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300", "#4a3aa7", "#e34948"];

type Rango = "3m" | "1a" | "todo" | "custom";

const hoyISO = () => new Date().toISOString().slice(0, 10);
function haceMeses(n: number): string {
  const d = new Date();
  d.setMonth(d.getMonth() - n);
  return d.toISOString().slice(0, 10);
}

export function ParametrosCauchyPage() {
  const qp = useQuery({ queryKey: ["cauchy", "puntos"], queryFn: getPuntosCauchy });
  const puntos = qp.data?.puntos ?? [];

  const [sel, setSel] = useState<string[] | null>(null);
  const [rango, setRango] = useState<Rango>("1a");
  const [desde, setDesde] = useState(haceMeses(12));
  const [hasta, setHasta] = useState(hoyISO());

  // Por defecto: los puntos activos (o todos si ninguno lo está).
  useEffect(() => {
    if (sel !== null || puntos.length === 0) return;
    const activos = puntos.filter((p) => p.activo).map(llaveCauchy);
    setSel(activos.length ? activos : puntos.map(llaveCauchy));
  }, [puntos, sel]);

  const elegidos = sel ?? [];
  const elegirRango = (r: Rango) => {
    setRango(r);
    if (r === "3m") { setDesde(haceMeses(3)); setHasta(hoyISO()); }
    if (r === "1a") { setDesde(haceMeses(12)); setHasta(hoyISO()); }
    if (r === "todo") { setDesde(""); setHasta(""); }
  };

  const qs = useQuery({
    queryKey: ["cauchy", "serie", elegidos, desde, hasta],
    queryFn: () => getSerieCauchy(elegidos, desde, hasta),
    enabled: elegidos.length > 0,
  });

  const excel = useMutation({ mutationFn: () => descargarSerieCauchyXlsx(elegidos, desde, hasta) });

  // Color e identidad por punto (posición en la lista completa).
  const info = useMemo(() => {
    const m = new Map<string, { p: PuntoSerieCauchy; color: string }>();
    puntos.forEach((p, i) => m.set(llaveCauchy(p), { p, color: PALETA[i % PALETA.length] }));
    return m;
  }, [puntos]);

  // Formato ancho por fecha: { fecha, "<llave>|location": v, "<llave>|scale": v }.
  const filas = useMemo(() => {
    const porFecha = new Map<string, Record<string, number | string>>();
    for (const r of qs.data?.serie ?? []) {
      const k = llaveCauchy(r);
      const f = porFecha.get(r.fecha) ?? { fecha: r.fecha };
      f[`${k}|location`] = r.location;
      f[`${k}|scale`] = r.scale;
      porFecha.set(r.fecha, f);
    }
    return [...porFecha.values()].sort((a, b) => String(a.fecha).localeCompare(String(b.fecha)));
  }, [qs.data]);

  const visibles = elegidos.filter((k) => info.has(k));
  const ventana = qp.data?.ventana_dias ?? 14;

  return (
    <div className="space-y-5 max-w-6xl">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Parámetros Cauchy (DART)</h1>
        <p className="text-sm text-slate-500 mt-1">
          Location y scale de la distribución Cauchy ajustada al DART horario (PML/PND MDA − MTR),
          en ventana móvil de {ventana} días. Cada fecha usa las horas de los {ventana} días que
          terminan ese día. Valores en MXN/MWh.
        </p>
      </div>

      {(qp.isError || qs.isError) && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">
          {((qp.error ?? qs.error) as Error)?.message}
        </div>
      )}

      {/* Filtros: una sola fila arriba de las gráficas */}
      <section className="bg-white border border-slate-200 rounded-lg p-3 flex flex-wrap items-center gap-x-5 gap-y-3">
        <div className="flex flex-wrap items-center gap-1.5">
          {puntos.length === 0 && qp.isSuccess && (
            <span className="text-sm text-slate-500">
              Aún no hay series calculadas. Agrega puntos en Configuración → Nodos Cauchy.
            </span>
          )}
          {puntos.map((p) => {
            const k = llaveCauchy(p);
            const on = elegidos.includes(k);
            const color = info.get(k)?.color;
            return (
              <button
                key={k}
                type="button"
                aria-pressed={on}
                title={`${p.tipo === "zona" ? "Zona (PND)" : "Nodo (PML)"} · ${p.sistema} · ${p.clave}`}
                onClick={() => setSel(on ? elegidos.filter((x) => x !== k) : [...elegidos, k])}
                className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${
                  on ? "border-slate-300 bg-white text-slate-800" : "border-slate-200 bg-slate-50 text-slate-400"
                }`}
              >
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: on ? color : "#cbd5e1" }} />
                {p.etiqueta}
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-1 text-xs">
          {([["3m", "3 meses"], ["1a", "1 año"], ["todo", "Todo"]] as const).map(([r, t]) => (
            <button
              key={r}
              type="button"
              onClick={() => elegirRango(r)}
              className={`rounded px-2 py-1 ${rango === r ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
            >
              {t}
            </button>
          ))}
          <input type="date" value={desde} onChange={(e) => { setDesde(e.target.value); setRango("custom"); }}
            className="ml-2 rounded border border-slate-300 px-2 py-1 text-xs" />
          <span className="text-slate-400">a</span>
          <input type="date" value={hasta} onChange={(e) => { setHasta(e.target.value); setRango("custom"); }}
            className="rounded border border-slate-300 px-2 py-1 text-xs" />
        </div>

        <button
          type="button"
          onClick={() => excel.mutate()}
          disabled={elegidos.length === 0 || excel.isPending}
          className="ml-auto inline-flex items-center gap-1.5 rounded border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          <Download className="h-4 w-4" />
          {excel.isPending ? "Generando…" : "Excel"}
        </button>
      </section>
      {excel.isError && <div className="text-sm text-red-600">{(excel.error as Error).message}</div>}

      {elegidos.length === 0 && puntos.length > 0 && (
        <div className="text-sm text-slate-500">Elige al menos un punto.</div>
      )}

      {elegidos.length > 0 && (
        <>
          <Grafica
            titulo="Scale"
            subtitulo="Ancho de la distribución: mide la volatilidad del DART."
            campo="scale"
            filas={filas}
            llaves={visibles}
            info={info}
            cargando={qs.isLoading}
          />
          <Grafica
            titulo="Location"
            subtitulo="Centro (mediana) del DART: positivo = el MDA quedó arriba del MTR."
            campo="location"
            filas={filas}
            llaves={visibles}
            info={info}
            cargando={qs.isLoading}
            cero
          />
          <Tabla filas={filas} llaves={visibles} info={info} />
        </>
      )}
    </div>
  );
}

type Info = Map<string, { p: PuntoSerieCauchy; color: string }>;

function Grafica({
  titulo, subtitulo, campo, filas, llaves, info, cargando, cero = false,
}: {
  titulo: string;
  subtitulo: string;
  campo: "location" | "scale";
  filas: Record<string, number | string>[];
  llaves: string[];
  info: Info;
  cargando: boolean;
  cero?: boolean;
}) {
  // Una marca por mes (el primer día con dato); cada 3 meses si el rango es largo.
  const ticks = useMemo(() => {
    const primeros: string[] = [];
    let mesPrev = "";
    for (const f of filas) {
      const mes = String(f.fecha).slice(0, 7);
      if (mes !== mesPrev) { primeros.push(String(f.fecha)); mesPrev = mes; }
    }
    const paso = primeros.length > 36 ? 6 : primeros.length > 15 ? 3 : 1;
    return primeros.filter((_, i) => i % paso === 0);
  }, [filas]);

  return (
    <section className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="mb-2">
        <div className="text-sm font-medium text-slate-800">{titulo} (MXN/MWh)</div>
        <div className="text-xs text-slate-500">{subtitulo}</div>
      </div>
      <div className="h-72">
        {cargando ? (
          <div className="h-full flex items-center justify-center text-sm text-slate-400">Cargando…</div>
        ) : filas.length === 0 ? (
          <div className="h-full flex items-center justify-center text-sm text-slate-400">
            Sin datos en el rango.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={filas} margin={{ top: 4, right: 28, bottom: 0, left: 4 }}>
              <CartesianGrid stroke="#f1f5f9" vertical={false} />
              <XAxis
                dataKey="fecha"
                ticks={ticks}
                interval={0}
                tickFormatter={fechaEje}
                tick={{ fontSize: 11, fill: "#64748b" }}
                tickLine={false}
                axisLine={{ stroke: "#e2e8f0" }}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "#64748b" }}
                tickLine={false}
                axisLine={false}
                width={56}
                tickFormatter={(v: number) => v.toLocaleString("es-MX", { maximumFractionDigits: 0 })}
              />
              {cero && <ReferenceLine y={0} stroke="#94a3b8" />}
              <Tooltip
                cursor={{ stroke: "#94a3b8", strokeWidth: 1 }}
                labelFormatter={(f) => fechaLarga(String(f))}
                formatter={(v: number, name: string) => [num(v), name]}
                contentStyle={{ fontSize: 12, borderRadius: 6, borderColor: "#e2e8f0" }}
              />
              {/* Texto de la leyenda en tinta neutra; el color lo lleva la marca. */}
              <Legend
                wrapperStyle={{ fontSize: 12 }}
                iconType="plainline"
                formatter={(v: string) => <span style={{ color: "#334155" }}>{v}</span>}
              />
              {llaves.map((k) => (
                <Line
                  key={k}
                  type="monotone"
                  dataKey={`${k}|${campo}`}
                  name={info.get(k)?.p.etiqueta ?? k}
                  stroke={info.get(k)?.color}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4, strokeWidth: 2, stroke: "#ffffff" }}
                  connectNulls={false}
                  isAnimationActive={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </section>
  );
}

// Orden de la tabla: "fecha" o "<llave>|location" / "<llave>|scale".
type Orden = { key: string; dir: "asc" | "desc" };

function Tabla({ filas, llaves, info }: { filas: Record<string, number | string>[]; llaves: string[]; info: Info }) {
  const [orden, setOrden] = useState<Orden>({ key: "fecha", dir: "desc" });

  // Si se apaga el punto por el que se ordenaba, vuelve a fecha.
  useEffect(() => {
    if (orden.key !== "fecha" && !llaves.some((k) => orden.key.startsWith(`${k}|`))) {
      setOrden({ key: "fecha", dir: "desc" });
    }
  }, [llaves, orden.key]);

  const ordenadas = useMemo(() => {
    const signo = orden.dir === "asc" ? 1 : -1;
    return [...filas].sort((a, b) => {
      const va = a[orden.key];
      const vb = b[orden.key];
      // Días sin dato de ese punto siempre al final, en cualquier dirección.
      if (va === undefined && vb === undefined) return 0;
      if (va === undefined) return 1;
      if (vb === undefined) return -1;
      const c = typeof va === "number" && typeof vb === "number"
        ? va - vb
        : String(va).localeCompare(String(vb));
      return c * signo;
    });
  }, [filas, orden]);

  // Primer clic: fecha → más reciente arriba; números → mayor arriba.
  const alternar = (key: string) =>
    setOrden((o) => (o.key === key ? { key, dir: o.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" }));

  const descripcion = (() => {
    if (orden.key === "fecha") return orden.dir === "desc" ? "más reciente arriba" : "más antiguo arriba";
    const [tipo, sistema, clave, campo] = orden.key.split("|");
    const etiqueta = info.get(`${tipo}|${sistema}|${clave}`)?.p.etiqueta ?? clave;
    const nombre = campo === "scale" ? "scale" : "location";
    return `${nombre} de ${etiqueta}, ${orden.dir === "desc" ? "mayor" : "menor"} arriba`;
  })();

  if (filas.length === 0) return null;
  return (
    <section className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
        <div className="text-sm font-medium text-slate-800">Valores diarios</div>
        <div className="text-xs text-slate-500">
          {filas.length.toLocaleString("es-MX")} días · ordenado por {descripcion}
        </div>
      </div>
      <div className="max-h-[28rem] overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs text-slate-500 sticky top-0">
            <tr>
              <th rowSpan={2} className="text-left font-medium px-4 py-2 bg-slate-50">
                <BotonOrden etiqueta="Fecha" k="fecha" orden={orden} onClick={alternar} />
              </th>
              {llaves.map((k) => (
                <th key={k} colSpan={2} className="text-center font-medium px-3 pt-2 pb-1 bg-slate-50 border-l border-slate-200">
                  <span className="inline-flex items-center gap-1.5 text-slate-700">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: info.get(k)?.color }} />
                    {info.get(k)?.p.etiqueta}
                  </span>
                </th>
              ))}
            </tr>
            <tr>
              {llaves.map((k) => (
                <EncabezadosPunto key={k} llave={k} orden={orden} onClick={alternar} />
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {ordenadas.map((f) => (
              <tr key={String(f.fecha)} className="hover:bg-slate-50">
                <td className="px-4 py-1.5 text-slate-700 whitespace-nowrap">{fechaCorta(String(f.fecha))}</td>
                {llaves.map((k) => (
                  <Celdas key={k} loc={f[`${k}|location`]} sc={f[`${k}|scale`]} />
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function EncabezadosPunto({ llave, orden, onClick }: {
  llave: string;
  orden: Orden;
  onClick: (key: string) => void;
}) {
  return (
    <>
      <th className="text-right font-medium px-3 pb-2 bg-slate-50 border-l border-slate-200">
        <BotonOrden etiqueta="Location" k={`${llave}|location`} orden={orden} onClick={onClick} />
      </th>
      <th className="text-right font-medium px-3 pb-2 bg-slate-50">
        <BotonOrden etiqueta="Scale" k={`${llave}|scale`} orden={orden} onClick={onClick} />
      </th>
    </>
  );
}

/** Encabezado ordenable, mismo estilo que /facturas-recibidas (▲ ▼ ↕). */
function BotonOrden({ etiqueta, k, orden, onClick }: {
  etiqueta: string;
  k: string;
  orden: Orden;
  onClick: (key: string) => void;
}) {
  const activo = orden.key === k;
  return (
    <button
      type="button"
      onClick={() => onClick(k)}
      title={`Ordenar por ${etiqueta.toLowerCase()}`}
      className={`inline-flex items-center gap-1 hover:text-slate-700 ${
        activo ? "text-slate-700 font-medium" : ""
      }`}
    >
      {etiqueta}
      <span className="text-[10px]">{activo ? (orden.dir === "asc" ? "▲" : "▼") : "↕"}</span>
    </button>
  );
}

function Celdas({ loc, sc }: { loc: number | string | undefined; sc: number | string | undefined }) {
  return (
    <>
      <td className="px-3 py-1.5 text-right tabular-nums text-slate-700 border-l border-slate-100">
        {typeof loc === "number" ? num(loc) : "—"}
      </td>
      <td className="px-3 py-1.5 text-right tabular-nums text-slate-700">
        {typeof sc === "number" ? num(sc) : "—"}
      </td>
    </>
  );
}

const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

function fechaEje(iso: string): string {
  const [a, m] = iso.split("-");
  return `${MESES[Number(m) - 1]} ${a.slice(2)}`;
}
function fechaCorta(iso: string): string {
  const [a, m, d] = iso.split("-");
  return `${d}/${m}/${a}`;
}
function fechaLarga(iso: string): string {
  const [a, m, d] = iso.split("-");
  return `${Number(d)} ${MESES[Number(m) - 1]} ${a}`;
}
function num(v: number): string {
  return v.toLocaleString("es-MX", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}
